import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { registerUserSettingsSync } from "libs/context/userSettingBridge";
import { syncAllUserSettingsFromServer } from "model/UserSettingModel";
import { hydrateHeaderSettings } from "store/layout/actions";

const UserSettingsSyncContext = createContext({
  settingsReady: false,
  syncGeneration: 0,
  refreshSettings: async () => null,
});

const applyDocumentHeaderSettings = (settings = {}) => {
  if (typeof document === "undefined" || !document.documentElement) return;

  if (settings.fontScale) {
    document.documentElement.setAttribute("data-font-scale", settings.fontScale);
  }

  if (settings.fontFamily) {
    document.documentElement.setAttribute("data-font-family", settings.fontFamily);
  }

  if (settings.fontColor) {
    document.documentElement.setAttribute("data-font-color", settings.fontColor);
  }

  if (settings.uiTheme) {
    document.documentElement.setAttribute("data-ui-theme", settings.uiTheme);
  }
};

/**
 * Single owner of GET `user/UserSetting` on bootstrap / login.
 * Other preference providers reuse localStorage + `settingsReady` / `syncGeneration`.
 */
export const HeaderSettingsSyncProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [settingsReady, setSettingsReady] = useState(false);
  const [syncGeneration, setSyncGeneration] = useState(0);

  const syncUserSettingsToApp = useCallback(async () => {
    if (!localStorage.getItem("authUser")) {
      setSettingsReady(true);
      return [];
    }

    try {
      const result = await syncAllUserSettingsFromServer();
      const syncedSettings = result?.headerSettings;

      if (syncedSettings) {
        applyDocumentHeaderSettings(syncedSettings);
        dispatch(hydrateHeaderSettings(syncedSettings));
      }

      setSyncGeneration((generation) => generation + 1);
      return Array.isArray(result?.updatedKeys) ? result.updatedKeys : [];
    } finally {
      setSettingsReady(true);
    }
  }, [dispatch]);

  useEffect(() => {
    const unregister = registerUserSettingsSync(syncUserSettingsToApp);
    syncUserSettingsToApp();

    return unregister;
  }, [syncUserSettingsToApp]);

  const value = useMemo(
    () => ({
      settingsReady,
      syncGeneration,
      refreshSettings: syncUserSettingsToApp,
    }),
    [settingsReady, syncGeneration, syncUserSettingsToApp]
  );

  return (
    <UserSettingsSyncContext.Provider value={value}>
      {children}
    </UserSettingsSyncContext.Provider>
  );
};

export const useUserSettingsSync = () => useContext(UserSettingsSyncContext);

export default HeaderSettingsSyncProvider;
